import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import TableHorizontal, { InfoObject } from "../misc/TableHorizontal";
import { ClearUpdatePasswordProfile, DeletePasswordProfile, ListPasswordProfiles, ListWordLists, UpdatePasswordProfile } from '../../store/PasswordProfileAction';
import { CorePasswordProfile, CoreWordList, FormPasswordProfile, WordListType } from '../../models/CorePasswordProfileModels';
import { CacheRequestState, EmptyRequestState, RequestState } from '../../models/RequestState';
import { RootState } from '../../store/store';
import StatusCode from '../../util/StatusCode';
import ModalComponent from '../misc/ModalComponent';
import { Button,Card,CardBody,Collapse,CardHeader } from 'reactstrap';
import IntInput from '../misc/IntInput';
import TextInput from '../misc/TextInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MultiSelectInput from '../misc/MultiSelectInput';
import { SearchableOptionItem } from '../../util/UtilityTypes';


const initialFormPasswordProfile = {
    id: undefined,
    name: "",
    tokenSets: [],
    minLength: 12,
    wordLists: undefined
}

enum Crud {
    UNDEFINED = "UNDEFINED",
    CREATE = "CREATE",
    UPDATE = "UPDATE",
    DELETE = "DELETE"
}

const validate = (profile: FormPasswordProfile, passwordProfiles: CacheRequestState<CorePasswordProfile[]>) => {
    const validation: string[] = [];

    const includedWordLists = profile.wordLists?.filter(x => x.type === WordListType.Included).length ?? 0;
    const countableTokenSets = profile.tokenSets.filter(x => x.length >= 1).length ?? 0;
    if ((countableTokenSets + includedWordLists) < 1) {
        validation.push('tokenSets')
    }

    if (profile.name.length < 2) {
        validation.push('name');
    }
    if (passwordProfiles.code === StatusCode.COMPLETE && passwordProfiles.data !== undefined
        && passwordProfiles.data.some(x => x.name === profile.name && x.id !== profile.id)) {
        validation.push('conflictingName');
    }

    if (profile.minLength < 6) {
        validation.push("minLength");
    }
    return validation;
}

const PasswordProfiles = () => {
    const { t } = useTranslation(['common']);
    const dispatch = useDispatch();

    const [crud, setCrud] = useState<Crud>(Crud.UNDEFINED);
    const [formPasswordProfile, setFormPasswordProfile] = useState<FormPasswordProfile>(initialFormPasswordProfile);
    const [shouldValidate, setShouldValidate] = useState<boolean>(false);
    const [openProfiles, setOpenProfiles] = useState<boolean>(true);

    const wordLists = useSelector<RootState, RequestState<CoreWordList[]>>(state => state.passwordProfile.wordLists)
    const passwordProfiles = useSelector<RootState, CacheRequestState<CorePasswordProfile[]>>(state => state.passwordProfile.passwordProfiles)
    const updatePasswordProfile = useSelector<RootState, EmptyRequestState>(state => state.passwordProfile.updatePasswordProfile)

    const validation = shouldValidate && validate(formPasswordProfile, passwordProfiles);

    useEffect(() => {
        ListPasswordProfiles()(dispatch);
        ListWordLists()(dispatch);
    }, [dispatch]);

    const updateProfile = () => {
        if (!shouldValidate) {
            setShouldValidate(true);
        }
        if (validation && !validation.length) {
            UpdatePasswordProfile(formPasswordProfile)(dispatch);
        }
    }

    const deleteProfile = () => {
        if (formPasswordProfile.id !== undefined) {
            DeletePasswordProfile(formPasswordProfile.id)(dispatch);
        }
    }

    const toggleModal = () => {
        setCrud(Crud.UNDEFINED);
        setFormPasswordProfile(initialFormPasswordProfile);
        setShouldValidate(false);
        if (updatePasswordProfile.code !== StatusCode.NONE) {
            ListPasswordProfiles(true)(dispatch);
            ClearUpdatePasswordProfile()(dispatch);
        }
    }

    const renderDeletePasswordProfile = () => {
        return (
            <ModalComponent
                isOpen={Boolean(Crud.DELETE)}
                header={t('passwordProfiles.modal.delete', { ns: 'common' })}
                cancel={t('action.cancel', { ns: 'common' })}
                confirm={t('yes', { ns: 'common' })}
                toggleModal={() => toggleModal()}
                update={() => deleteProfile()}
                saveStatus={updatePasswordProfile.code}
            >
                {t('passwordProfiles.modal.deleteProfile', { ns: 'common', profileName: formPasswordProfile.name })}
            </ModalComponent>
        )
    }

    const renderTokenSets = () => {
        if (formPasswordProfile?.tokenSets?.length) {
            return formPasswordProfile?.tokenSets.map((x, index) =>
                <div key={index} className="tokensets flex align-items-baseline invalid-option">
                    <TextInput
                        name={`tokenSets${index}`}
                        value={x}
                        updateValue={(_, value) =>
                            setFormPasswordProfile({
                                ...formPasswordProfile,
                                tokenSets: formPasswordProfile.tokenSets.map((t, i) =>
                                    i === index ? value : t
                                )
                            })
                        }
                    />
                    <Button onClick={() =>
                        setFormPasswordProfile({
                            ...formPasswordProfile,
                            tokenSets: formPasswordProfile.tokenSets.filter((_, i) => i !== index)
                        })
                    } className="btn-icon"><FontAwesomeIcon icon="trash" className="icon" /></Button>
                </div>
            )
        } else {
            return <span className="invalid-option">
                <p>{t('passwordProfiles.modal.tokenSets.noTokenSets', { ns: 'common' })}</p>
                {validation && validation.indexOf('tokenSets') !== -1 && <span className="invalid-text">{t('passwordProfiles.modal.invalidFeedback.tokenSets', { ns: 'common' })}</span>}
            </span>
        }
    }

    const renderWordLists = () => {
        let wordListOptions: SearchableOptionItem<string>[] = [];

        if (wordLists.code === StatusCode.COMPLETE) {
            wordListOptions = wordLists.data
                .map(x => ({ value: x.id, label: x.name }))
        }
        return wordListOptions;
    }

    const setWordLists = (value: string[] | null | undefined, type: WordListType) => {
        const mergeWordLists = [
            ...(formPasswordProfile.wordLists?.filter(x => x.type !== type) ?? []),
            ...(value?.map(x => ({ wordListId: x, type })) ?? [])
        ];

        setFormPasswordProfile({
            ...formPasswordProfile,
            wordLists: mergeWordLists
        });
    }

    const renderErrorMessage = () => {
        if (updatePasswordProfile.code === StatusCode.ERROR && updatePasswordProfile.error?.response?.data === "ConflictingName")
            return t('passwordProfiles.modal.errorMsg', { ns: 'common' })
    }

    const renderUpdatePasswordProfile = () => {
        return (
            <ModalComponent
                isOpen={Boolean(Crud.CREATE) || Boolean(Crud.UPDATE)}
                header={crud === Crud.CREATE ? t('passwordProfiles.modal.create', { ns: 'common' }) : t('passwordProfiles.modal.update', { ns: 'common' })}
                cancel={t('action.cancel', { ns: 'common' })}
                confirm={t('action.save', { ns: 'common' })}
                disabled={validation && validation.length > 0}
                toggleModal={() => toggleModal()}
                update={() => updateProfile()}
                saveStatus={updatePasswordProfile.code}
                errorMsg={renderErrorMessage()}
            >
                <div className="create-password-profile-modal">
                    <h5>{t('passwordProfiles.modal.name', { ns: 'common' })}</h5>
                    <TextInput
                        name="name"
                        placeholder={t('passwordProfiles.modal.namePlaceholder', { ns: 'common' })}
                        value={formPasswordProfile.name}
                        updateValue={(name, value) => setFormPasswordProfile({ ...formPasswordProfile, name: value })}
                        invalid={(validation && validation.indexOf('name') !== -1) || (validation && validation.indexOf('conflictingName') !== -1)}
                        invalidFeedback={(validation && validation.indexOf('name') !== -1) ? t('passwordProfiles.modal.invalidFeedback.name')
                            : (validation && validation.indexOf('conflictingName') !== -1) ? t('passwordProfiles.modal.invalidFeedback.conflictingName') : ""}
                    />

                    <div className={validation && validation.indexOf('tokenSets') !== -1 ? "invalid" : undefined}>

                        <span className="invalid-option">
                            <h5>{t('passwordProfiles.modal.includedWordLists.title')}</h5>
                            <MultiSelectInput
                                items={renderWordLists()}
                                onChange={(_, value) => setWordLists(value, WordListType.Included)}
                                name="wordLists"
                                defaultText={t('passwordProfiles.modal.includedWordLists.select')}
                                selected={
                                    formPasswordProfile.wordLists
                                        ?.filter(x => x.type === WordListType.Included)
                                        .map(x => x.wordListId)
                                    || undefined
                                }
                            />
                        </span>

                        <h5>{t('passwordProfiles.modal.tokenSets.title', { ns: 'common' })}</h5>
                        <p>{t('passwordProfiles.modal.tokenSets.description', { ns: 'common' })}</p>
                        {renderTokenSets()}
                        <Button className="mb-3" onClick={() => setFormPasswordProfile({ ...formPasswordProfile, tokenSets: [...formPasswordProfile.tokenSets, ""] })}>{t('passwordProfiles.modal.tokenSets.addTokenSet', { ns: 'common' })}</Button>
                    </div>

                    <IntInput
                        title={t('passwordProfiles.modal.minLength', { ns: 'common' })}
                        name="minLength"
                        value={formPasswordProfile?.minLength ?? 0}
                        updateValue={(name, value) => setFormPasswordProfile({ ...formPasswordProfile, minLength: value })}
                        invalid={validation && validation.indexOf('minLength') !== -1}
                        invalidFeedback={t('passwordProfiles.modal.invalidFeedback.minLength')}
                        titleLarge={true}
                    />
                </div>

            </ModalComponent>
        )
    }

    const renderProfiles = (): InfoObject[] => {
        if (passwordProfiles.code === StatusCode.COMPLETE || (passwordProfiles.code === StatusCode.PENDING)) {
            if (passwordProfiles.data === undefined) {
                return [];
            }
            return passwordProfiles.data.map(x => {
                return ({
                    id: x.id,
                    data: [
                        { value: x.name },
                        { value: x.tokenSets.length },
                        { value: x.wordLists?.length },
                        { value: x.minLength },
                        { value: <Button className="btn-icon" title={t('passwordProfiles.edit.update', { ns: 'common' })} onClick={() => (setFormPasswordProfile(x), setCrud(Crud.UPDATE), setShouldValidate(true))}><FontAwesomeIcon icon="pen" className="icon" /></Button>, className: "edit-icon-btn" },
                        { value: <Button className="btn-icon" title={t('passwordProfiles.edit.delete', { ns: 'common' })} onClick={() => (setFormPasswordProfile(x), setCrud(Crud.DELETE))}><FontAwesomeIcon icon="trash" className="icon" /></Button>, className: "edit-icon-btn" }
                    ]
                })
            })
        }
        return [];
    }

    return (
            <Card className="mb-3 edit_module"> 
                <CardHeader>
                    <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpenProfiles(!openProfiles)}>{openProfiles ? '-' : '+'}</Button>
                    {t('passwordProfiles.title', { ns: 'common' })}
                </CardHeader>
                <Collapse isOpen={openProfiles}> 
                    <CardBody> 
                        <TableHorizontal
                            header={[
                                { value: t('genericTableHeaders.name', { ns: 'common' }) },
                                { value: t('passwordProfiles.tableHeader.tokenSetsCount', { ns: 'common' }) },
                                { value: t('passwordProfiles.tableHeader.wordListsCount', { ns: 'common' }) },
                                { value: t('passwordProfiles.tableHeader.minLength', { ns: 'common' }) },
                                { value: t('genericTableHeaders.edit', { ns: 'common' }), colSpan: 2, dataLabel: "" }

                            ]}
                            info={renderProfiles()}
                            error={passwordProfiles.code === StatusCode.ERROR}
                            searching={passwordProfiles.code === StatusCode.PENDING}
                            noResMsg={t('passwordProfiles.notFound.noRes', { ns: 'common' })}
                        />

                        <Button onClick={() => setCrud(Crud.CREATE)}>{t('passwordProfiles.btnNewProfile', { ns: 'common' })}</Button>
                        {(crud === Crud.CREATE || crud === Crud.UPDATE) && renderUpdatePasswordProfile()}

                        {crud === Crud.DELETE && renderDeletePasswordProfile()}
                    </CardBody>
                </Collapse>
            </Card>
    )
}

export default PasswordProfiles;
